import { useState, useEffect } from 'react';
import { Search, MapPin, Phone, Globe, Mail, Filter, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { ShareMenu } from '@/components/ShareMenu';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";

const DirectoryPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [selectedCategory, searchTerm]);

  const loadData = async () => {
    try {
      const catRes = await api.get('/directory/categories');
      setCategories(catRes.data.categories || []);
    } catch (e) { console.error(e); }
  };

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      const res = await api.get(`/directory?${params.toString()}`);
      setBusinesses(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Group categories by parent for the dropdown
  const parentCategories = [...new Set(categories.map(c => c.split(' - ')[0]))].sort();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="directory-page">
      <SEOPage.Directory />
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/95" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <Badge className="mb-4 bg-sky-500/20 text-sky-400 border-sky-500/30">Local Businesses</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Business Directory
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Discover local businesses in Paisley. From restaurants and shops to professional services and healthcare.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-slate-200 sticky top-20 z-30" data-testid="directory-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search businesses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200"
                data-testid="directory-search"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-64 bg-slate-50" data-testid="directory-category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-slate-500">
              {businesses.length} {businesses.length === 1 ? 'business' : 'businesses'} found
            </div>
          </div>
        </div>
      </section>

      {/* Business Listings */}
      <section className="py-12" data-testid="directory-listings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-slate-100 rounded w-1/2 mb-4" />
                    <div className="h-3 bg-slate-100 rounded mb-2" />
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : businesses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((biz) => (
                <Card key={biz.id} className="hover:shadow-md transition-shadow h-full" data-testid={`biz-card-${biz.id}`}>
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="mb-3">
                      <Badge variant="secondary" className="text-xs mb-2">{biz.category}</Badge>
                      <h3 className="font-heading text-lg font-semibold text-slate-900">
                        {biz.slug ? (
                          <Link to={`/directory/${biz.slug}`} className="hover:text-sky-600 transition-colors" data-testid={`biz-detail-link-${biz.id}`}>
                            {biz.business_name}
                          </Link>
                        ) : biz.business_name}
                      </h3>
                    </div>
                    <p className="text-slate-500 text-sm mb-4 flex-1">{biz.description}</p>
                    <div className="space-y-2 text-sm border-t border-slate-100 pt-4">
                      <div className="flex items-start gap-2 text-slate-600">
                        <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span>{biz.address}</span>
                      </div>
                      {biz.phone && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <a href={`tel:${biz.phone.replace(/\s/g, '')}`} className="hover:text-sky-600">{biz.phone}</a>
                        </div>
                      )}
                      {biz.website && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Globe className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <a href={biz.website} target="_blank" rel="noopener noreferrer" className="hover:text-sky-600 truncate">{biz.website.replace('https://', '')}</a>
                        </div>
                      )}
                      {biz.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <a href={`mailto:${biz.email}`} className="hover:text-sky-600 truncate">{biz.email}</a>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                      <ShareMenu
                        title={biz.business_name}
                        description={biz.category ? `${biz.category} in Paisley` : 'Local Paisley business'}
                        url={biz.slug ? `https://paisleyscotland.com/directory/${biz.slug}` : (biz.website || 'https://paisleyscotland.com/directory')}
                        testId={`share-biz-${biz.id}`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No businesses found</h3>
              <p className="text-slate-500">Try adjusting your search or category filter.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DirectoryPage;
